import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getEmployeeDetail } from "../../services/aiSalesDiagnosisApi.jsx";
import NudgePanel from "./NudgePanel.jsx";
import DashboardPanelSwitcher from "../Common/DashboardPanelSwitcher";
import "./AISalesDiagnosis.css";

function getColorClass(value, type = "default") {
  if (value == null) return "";

  switch (type) {
    case "percentage":
      if (value < 50) return "danger";
      if (value < 80) return "warning";
      return "success";
    case "revenue":
      if (value < 0) return "danger";
      if (value <= 80) return "warning";
      return "success";
    case "ratio":
      if (value < 0.6) return "danger";
      if (value < 0.9) return "warning";
      return "success";
    default:
      return "";
  }
}

const PAGE_SIZE = 20;

export default function EmployeeDetail() {
  const { id: employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("all");
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNudge, setShowNudge] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [doctorPage, setDoctorPage] = useState(1);
  const [doctorLoading, setDoctorLoading] = useState(false);
  const [doctorList, setDoctorList] = useState([]);
  const [doctorCounts, setDoctorCounts] = useState({ all: 0, met: 0, missed: 0 });

  const params = new URLSearchParams(location.search);
  const yearmonth = params.get("yearmonth");
  const division = params.get("division");
  const territory = params.get("territory");

  const handleBack = () => navigate(-1);

  const handleNudgeClick = (emp) => {
    setSelectedEmployee(emp);
    setShowNudge(true);
  };
  const handleClose = () => {
    setShowNudge(false);
    setSelectedEmployee(null);
  };

  useEffect(() => {
    loadEmployeeDetail(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const loadEmployeeDetail = async (page) => {
    try {
      page === 1 ? setLoading(true) : setDoctorLoading(true);
      setError(null);

      const data = await getEmployeeDetail(employeeId, yearmonth, division, territory, page, PAGE_SIZE, activeTab);
      if (page === 1) {
        setEmployee(data);
      }

      let list = Array.isArray(data?.doctorCoverage) ? data.doctorCoverage : data?.doctorCoverage?.data || [];

      // Client-side filter fallback — in case backend returns unfiltered list
      if (activeTab === "missed") {
        list = list.filter(d => d?.status === "Missed" || d?.status === "missed" || d?.status === "Not Met");
      } else if (activeTab === "met") {
        list = list.filter(d => d?.status === "Met" || d?.status === "met" || d?.status === "Compliant");
      }

      setDoctorList(list);

      if (data?.doctorCoverage?.counts) {
        setDoctorCounts(data.doctorCoverage.counts);
      }

      setDoctorPage(page);
    } catch (err) {
      setError("Failed to load employee details");
    } finally {
      setLoading(false);
      setDoctorLoading(false);
    }
  };

  const loadDoctors = async (page, tab) => {
    try {
      setDoctorLoading(true);
      const data = await getEmployeeDetail(employeeId, yearmonth, division, territory, page, PAGE_SIZE, tab ?? activeTab);
      let list = Array.isArray(data?.doctorCoverage) ? data.doctorCoverage : data?.doctorCoverage?.data || [];

      // Client-side filter fallback — in case backend returns unfiltered list
      const filterTab = tab ?? activeTab;
      if (filterTab === "missed") {
        list = list.filter(d => d?.status === "Missed" || d?.status === "missed" || d?.status === "Not Met");
      } else if (filterTab === "met") {
        list = list.filter(d => d?.status === "Met" || d?.status === "met" || d?.status === "Compliant");
      }

      setDoctorList(list);
      if (data?.doctorCoverage?.counts) {
        setDoctorCounts(data.doctorCoverage.counts);
      }
      setDoctorPage(page);
    } finally {
      setDoctorLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setDoctorPage(1);
    loadDoctors(1, tab);
  };

  const totalDoctorsForTab = activeTab === "missed" ? doctorCounts.missed : activeTab === "met" ? doctorCounts.met : doctorCounts.all;
  const totalPages = Math.ceil((totalDoctorsForTab || 0) / PAGE_SIZE);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    loadDoctors(newPage, activeTab);
    document.getElementById("doctor-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const PaginationBar = () => {
    if (totalPages <= 1) return null;

    const buildPages = () => {
      const pages = [];
      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i += 1) pages.push(i);
      } else {
        pages.push(1);
        if (doctorPage > 3) pages.push("...");
        for (let i = Math.max(2, doctorPage - 1); i <= Math.min(totalPages - 1, doctorPage + 1); i += 1) {
          pages.push(i);
        }
        if (doctorPage < totalPages - 2) pages.push("...");
        pages.push(totalPages);
      }
      return pages;
    };

    const start = (doctorPage - 1) * PAGE_SIZE + 1;
    const end = Math.min(doctorPage * PAGE_SIZE, totalDoctorsForTab);

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "16px",
          padding: "12px 0",
          borderTop: "1px solid #eee",
        }}
      >
        <span style={{ fontSize: "13px", color: "#888" }}>
          Showing {start}–{end} of {totalDoctorsForTab} doctors
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <button
            onClick={() => handlePageChange(doctorPage - 1)}
            disabled={doctorPage === 1 || doctorLoading}
            style={{
              padding: "6px 12px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              background: doctorPage === 1 ? "#f5f5f5" : "#fff",
              color: doctorPage === 1 ? "#ccc" : "#333",
              cursor: doctorPage === 1 ? "not-allowed" : "pointer",
              fontSize: "13px",
              fontWeight: "500",
            }}
          >
            ← Prev
          </button>

          {buildPages().map((p, i) =>
            p === "..." ? (
              <span key={`ellipsis-${i}`} style={{ padding: "0 4px", color: "#aaa" }}>
                …
              </span>
            ) : (
              <button
                key={p}
                onClick={() => handlePageChange(p)}
                disabled={doctorLoading}
                style={{
                  width: "34px",
                  height: "34px",
                  borderRadius: "6px",
                  border: p === doctorPage ? "none" : "1px solid #ddd",
                  background: p === doctorPage ? "#1a2b4a" : "#fff",
                  color: p === doctorPage ? "#fff" : "#333",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: p === doctorPage ? "700" : "400",
                }}
              >
                {p}
              </button>
            )
          )}

          <button
            onClick={() => handlePageChange(doctorPage + 1)}
            disabled={doctorPage === totalPages || doctorLoading}
            style={{
              padding: "6px 12px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              background: doctorPage === totalPages ? "#f5f5f5" : "#fff",
              color: doctorPage === totalPages ? "#ccc" : "#333",
              cursor: doctorPage === totalPages ? "not-allowed" : "pointer",
              fontSize: "13px",
              fontWeight: "500",
            }}
          >
            Next →
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="emcure-ai-sales-diagnosis-embed">
        <div className="empty-state">
          <p>Loading employee details...</p>
        </div>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="emcure-ai-sales-diagnosis-embed">
        <div className="empty-state">
          <p>{error || "Employee not found"}</p>
          <button className="btn" onClick={handleBack}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { performanceMetrics, aiDiagnosis } = employee;
  const salesPerc = performanceMetrics?.salesAch?.percentage ?? 0;
  const fwdRatio = (performanceMetrics?.fwd?.current ?? 0) / (performanceMetrics?.fwd?.target || 1);
  const coveragePerc = performanceMetrics?.coverage?.percentage ?? 0;
  const callRatio = (performanceMetrics?.callAverage?.value ?? 0) / (performanceMetrics?.callAverage?.target || 1);
  const freqPerc = performanceMetrics?.frequencyCompliance?.percentage ?? 0;

  return (
    <div className="emcure-ai-sales-diagnosis-embed">
      <header className="header header-employee-detail">
        <button className="back-btn" onClick={handleBack}>
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>
        <div className="header-employee-actions">
          <button
            className="btn"
            onClick={(e) => {
              e.stopPropagation();
              handleNudgeClick(employee);
            }}
          >
            Nudge
          </button>
          <DashboardPanelSwitcher direction="down" />
        </div>
      </header>

      <NudgePanel show={showNudge} onClose={handleClose} employeeData={selectedEmployee} />

      <div className="container">
        <div className="employee-header">
          <div className="employee-avatar">
            {employee.avatar ? <img src={employee.avatar} alt={employee.name} /> : employee.name.split(" ").map((n) => n[0]).join("")}
          </div>
          <div className="employee-details">
            <h1>{employee.name}</h1>
            <p>
              HQ: {employee.hq} · Manager: {employee.manager}
            </p>
          </div>
        </div>

        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-card-header">Sales Ach%</div>
            <div className="metric-card-value">{salesPerc}%</div>
            <div className="metric-card-subtitle">
              <span style={{ display: "block", fontWeight: 400 }}>Actual sales: {performanceMetrics?.salesAch?.actual}L</span>
              <span style={{ display: "block", fontWeight: 400 }}>Target sales: {performanceMetrics?.salesAch?.target}L</span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-card-header">Field Working Days</div>
            <div className={`metric-card-value ${getColorClass(fwdRatio, "ratio")}`}>
              {performanceMetrics?.fwd?.current}/{performanceMetrics?.fwd?.target}
            </div>
            <div className="metric-card-subtitle">{performanceMetrics?.fwd?.daysLeft ?? 0} days missed</div>
          </div>

          <div className="metric-card">
            <div className="metric-card-header">Coverage</div>
            <div className={`metric-card-value ${getColorClass(coveragePerc, "percentage")}`}>{coveragePerc}%</div>
            <div className="metric-card-subtitle">
              {performanceMetrics?.coverage?.met} / {performanceMetrics?.coverage?.total} Drs
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-card-header">Call Average</div>
            <div className={`metric-card-value ${getColorClass(callRatio, "ratio")}`}>{performanceMetrics?.callAverage?.value}</div>
            <div className="metric-card-subtitle">Target: {performanceMetrics?.callAverage?.target}</div>
          </div>

          <div className="metric-card">
            <div className="metric-card-header">Frequency Compliance</div>
            <div className={`metric-card-value ${getColorClass(freqPerc, "percentage")}`}>{freqPerc}%</div>
            <div className="metric-card-subtitle">
              {performanceMetrics?.frequencyCompliance?.met} / {performanceMetrics?.frequencyCompliance?.total}
            </div>
          </div>
        </div>

        <div className="ai-diagnosis">
          <div className="ai-diagnosis-header">
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path
                fillRule="evenodd"
                d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                clipRule="evenodd"
              />
            </svg>
            AI Diagnosis
          </div>
          <div className="ai-diagnosis-content">
            <div className="diagnosis-item">
              <span className="diagnosis-label primary">Primary Issue: </span>
              {aiDiagnosis?.primary}
            </div>
            <div className="diagnosis-item">
              <span className="diagnosis-label secondary">Secondary: </span>
              {aiDiagnosis?.secondary}
            </div>
          </div>
        </div>

        <div id="doctor-section" className="section-header">
          <h3 className="section-title">DOCTOR COVERAGE DETAIL</h3>
        </div>

        <div className="tabs">
          <button className={`tab ${activeTab === "all" ? "active" : ""}`} onClick={() => handleTabChange("all")}>
            All ({doctorCounts.all})
          </button>
          <button className={`tab ${activeTab === "missed" ? "active" : ""}`} onClick={() => handleTabChange("missed")}>
            Missed ({doctorCounts.missed})
          </button>
          <button className={`tab ${activeTab === "met" ? "active" : ""}`} onClick={() => handleTabChange("met")}>
            Met ({doctorCounts.met})
          </button>
        </div>

        <div className="table-container" style={{ position: "relative" }}>
          {doctorLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                borderRadius: "8px",
              }}
            >
              <span style={{ fontSize: "14px", color: "#555" }}>Loading...</span>
            </div>
          )}

          <table>
            <thead>
              <tr>
                <th>Doctor</th>
                <th>Speciality</th>
                <th>Category</th>
                <th>Status</th>
                <th>Visits [YF / Actual]</th>
                <th>Last Visit</th>
                <th>Est. Potential</th>
              </tr>
            </thead>
            <tbody>
              {doctorList.length === 0 && !doctorLoading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: "24px", color: "#aaa" }}>
                    No doctors found
                  </td>
                </tr>
              ) : (
                doctorList.map((doctor, idx) => (
                  <tr key={doctor?.uid ?? idx}>
                    <td className="employee-name">{doctor?.name}</td>
                    <td>{doctor?.specialty ?? "—"}</td>
                    <td>
                      <span className={`badge ${doctor?.category === "A" ? "badge-danger" : "badge-success"}`}>{doctor?.category}</span>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          doctor?.status === "Met" || doctor?.status === "Compliant" ? "badge-success" : "badge-danger"
                        }`}
                      >
                        {doctor?.status}
                      </span>
                    </td>
                    <td>
                      {typeof doctor?.visits === "object" ? `${doctor?.visits?.actual} / ${doctor?.visits?.target}` : doctor?.visits}
                    </td>
                    <td>{doctor?.lastVisit}</td>
                    <td>{doctor?.potential ?? "N/A"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar />
      </div>
    </div>
  );
}

